// NPM packages
import React, { useState, useRef } from 'react';
import { Card, Modal, Box, Backdrop, Button } from '@mui/material';
import { Typography } from '@mui/material';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

// All other imports
import { VideoResourceStoryblok } from 'types/storyblokTypes';
import { storyblokEditable } from '@storyblok/react';
import {
  IMAGE_COLUMN_WIDTH_PX,
  RESOURCE_COMPONENT_COLUMN_GAP as COLUMN_GAP,
} from '../utils';
import ResourceInformation from '../ResourceInformation';

import { useLocation } from 'react-router-dom';
import Link from 'components/core/Link';

// All other imports

const Root = styled.div(({ theme }) => {
  return {
    minWidth: theme.maxToolWidth,
    marginTop: '1.75rem',
    display: 'grid',
    gridTemplateColumns: `${IMAGE_COLUMN_WIDTH_PX}px 55rem`,
    gap: `${COLUMN_GAP}rem`,
  };
});

const VideoContentCard = styled(Card)({
  height: `${IMAGE_COLUMN_WIDTH_PX * 0.565574}px`,
  width: `${IMAGE_COLUMN_WIDTH_PX}px`,
});

const Video = styled.iframe({
  height: `${IMAGE_COLUMN_WIDTH_PX * 0.565574}px`,
  width: `${IMAGE_COLUMN_WIDTH_PX}px`,
});

const VideoOnlyContentCard = styled(Card)({
  width: '100%',
  padding: 0,
  '& .MuiCard-root': {
    padding: 0,
  },
});

const VideoContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const ExpandButton = styled(Button)({
  position: 'absolute',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#1976d2',
  color: 'white',
  minWidth: 'auto',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 'bold',
  zIndex: 1,
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  border: '2px solid white',
  borderRadius: '8px',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#1565c0',
    transform: 'translateX(-50%) scale(1.05)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
  },
});

const VideoFullWidth = styled.iframe({
  width: '100%',
  aspectRatio: '16/9',
  height: 'auto',
  display: 'block',
  border: 'none',
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 1,
  borderRadius: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '90vw',
  height: '90vh',
  overflow: 'hidden',
};

export interface VideoResourceProps {
  blok: VideoResourceStoryblok;
  className?: string;
}

function VideoResource(props: VideoResourceProps): React.ReactElement | null {
  const { blok, className } = props;
  const thumbnailExists = Boolean(blok.videoUrl);
  const [open, setOpen] = useState<boolean>(false);
  const videoRef = useRef<HTMLIFrameElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const handleOpen = (): void => {
    setOpen(true);
    // Move the iframe from page container to modal container after modal opens
    setTimeout(() => {
      if (videoRef.current && modalContainerRef.current && pageContainerRef.current) {
        // Remove from page container
        if (pageContainerRef.current.contains(videoRef.current)) {
          pageContainerRef.current.removeChild(videoRef.current);
        }
        // Add to modal container
        modalContainerRef.current.appendChild(videoRef.current);
        // Update styles for modal display
        videoRef.current.style.width = '100%';
        videoRef.current.style.height = '100%';
      }
    }, 100);
  };

  const handleClose = (): void => {
    // Move the iframe back from modal container to page container before modal closes
    if (videoRef.current && pageContainerRef.current && modalContainerRef.current) {
      // Remove from modal container
      if (modalContainerRef.current.contains(videoRef.current)) {
        modalContainerRef.current.removeChild(videoRef.current);
      }
      // Add back to page container
      pageContainerRef.current.appendChild(videoRef.current);
      // Reset styles for page display
      videoRef.current.style.width = '100%';
      videoRef.current.style.height = 'auto';
      videoRef.current.style.aspectRatio = '16/9';
    }
    setOpen(false);
  };

  // Modify YouTube URL to enable API control
  const getApiEnabledUrl = (url: string | undefined): string => {
    if (!url) return '';
    if (url.includes('enablejsapi=1')) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}enablejsapi=1`;
  };
  const webappPathname = useLocation().pathname;
  const isMinimalPath = webappPathname.startsWith('/minimal');

  // Create the iframe element that will be moved between containers
  const videoIframe = (
    <VideoFullWidth
      ref={videoRef}
      src={getApiEnabledUrl(blok.videoUrl)}
      title="YouTube video player"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      style={{
        width: '100%',
        height: 'auto',
        aspectRatio: '16/9',
        display: 'block',
        border: 'none',
      }}
    />
  );

  // If minimal path, render title as link instead of video. This is because minimnal
  // is used from our jupyter notebook when we open help pages (want them inside the notebook rendered 
  // without header/nav/footer). But, because jupyter lab's help implementation opens webpages inside iframes:
  // "Because of iframe-in-iframe nesting, the outer iframe (JupyterLab) applies restrictions (sandbox, CSP, 
  // same-origin rules). YouTube blocks being embedded inside iframes that are already sandboxed or 
  // don’t have the right allow attributes — result: black player window."
  if (isMinimalPath) {
    return (
        <Link 
          to={blok.videoUrl}
          openNewTab
          className={className}
        >
          {blok.title}
        </Link>
    );
  }

  return (
    // letting video resources with no description be larger (used on quick guides)
    blok.description === ""
      ? 
      <>
        <Typography>{blok.title}</Typography>
        <VideoOnlyContentCard variant="elevation" raised>
          <VideoContainer>
            <ExpandButton onClick={handleOpen} variant="contained">
              Expand
            </ExpandButton>
            <div ref={pageContainerRef}>
              {videoIframe}
            </div>
          </VideoContainer>
        </VideoOnlyContentCard>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="video-modal"
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ 
            backdrop: { 
              timeout: 500,
              onClick: handleClose 
            } 
          }}
        >
          <Box sx={modalStyle}>
            <div ref={modalContainerRef} style={{ width: '100%', height: '100%' }} />
          </Box>
        </Modal>
      </>
      : 
    <Root className={className} {...storyblokEditable(blok)}>
      <VideoContentCard variant="elevation" raised>
        {thumbnailExists ? (
          <Video
            src={getApiEnabledUrl(blok.videoUrl)}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          <div></div>
        )}
      </VideoContentCard>
      <ResourceInformation blok={blok} />
    </Root>
  );
}

export default observer(VideoResource);
